import React, { useState, useEffect } from 'react';
import { collaborationAPI } from '../services/api';
import './ManageCollaborators.css';

const ManageCollaborators = ({ project, currentUser, onClose, onUpdate }) => {
  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedNewOwner, setSelectedNewOwner] = useState('');

  const isOwner = project.isOwner;

  useEffect(() => {
    fetchMembers();
    fetchPendingInvites();
  }, [project._id]);

  const fetchMembers = async () => {
    try {
      const response = await collaborationAPI.getProjectMembers(project._id);
      setMembers(response.data.data.members);
      setLoading(false);
    } catch (err) {
      setError('Failed to load members');
      setLoading(false);
    }
  };

  const fetchPendingInvites = async () => {
    try {
      const response = await collaborationAPI.getProjectInvites(project._id);
      setPendingInvites(response.data.data.invites);
    } catch (err) {
      console.error('Failed to load pending invites:', err);
    }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    setError('');
    setSuccess('');

    try {
      const response = await collaborationAPI.sendInvite(project._id, inviteEmail);
      setSuccess(response.data.message);
      setInviteEmail('');
      fetchPendingInvites();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send invitation');
    }

    setInviting(false);
  };

  const handleRevokeInvite = async (inviteId) => {
    try {
      await collaborationAPI.revokeInvite(project._id, inviteId);
      setSuccess('Invitation revoked');
      fetchPendingInvites();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to revoke invitation');
    }
  };

  const handleResendInvite = async (email, inviteId) => {
    try {
      // First revoke the old invite
      await collaborationAPI.revokeInvite(project._id, inviteId);

      // Then send a new invite
      const response = await collaborationAPI.sendInvite(project._id, email);
      setSuccess(response.data.message);
      fetchPendingInvites();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend invitation');
    }
  };

  const handleRemoveMember = async (userId, userName) => {
    if (!window.confirm(`Remove ${userName} from this project?`)) {
      return;
    }

    try {
      await collaborationAPI.removeMember(project._id, userId);
      setSuccess(`${userName} removed from project`);
      fetchMembers();
      onUpdate();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to remove member');
    }
  };

  const handleLeaveProject = async () => {
    if (!window.confirm('Are you sure you want to leave this project?')) {
      return;
    }

    try {
      await collaborationAPI.leaveProject(project._id);
      setSuccess('You have left the project');
      onUpdate();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to leave project');
    }
  };

  const handleTransferOwnership = async () => {
    if (!selectedNewOwner) {
      setError('Please select a new owner');
      return;
    }

    const newOwner = members.find(m => m.userId === selectedNewOwner);
    if (!window.confirm(`Transfer ownership to ${newOwner.name}? You will become a collaborator.`)) {
      return;
    }

    try {
      const response = await collaborationAPI.transferOwnership(project._id, selectedNewOwner);
      setSuccess(response.data.message);
      setShowTransferModal(false);
      fetchMembers();
      onUpdate();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to transfer ownership');
    }
  };

  const collaborators = members.filter(m => m.role === 'collaborator');
  const owner = members.find(m => m.role === 'owner');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content manage-collaborators-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Manage Collaborators</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}

          {loading ? (
            <div className="spinner"></div>
          ) : (
            <>
              {/* Invite Form */}
              <div className="invite-section">
                <h3>Invite Collaborator</h3>
                <form onSubmit={handleSendInvite} className="invite-form">
                  <input
                    type="email"
                    placeholder="Enter email address"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    required
                    className="form-control"
                  />
                  <button type="submit" className="btn btn-primary" disabled={inviting}>
                    {inviting ? 'Sending...' : 'Send Invite'}
                  </button>
                </form>
                <p className="help-text">
                  Enter an email address to send an invitation. The invitee will receive an email with instructions. If they already have an account, they'll also see the invitation in their dashboard when they log in.
                </p>
              </div>

              {/* Pending Invites */}
              {pendingInvites.length > 0 && (
                <div className="pending-invites-section">
                  <h3>Pending Invitations ({pendingInvites.length})</h3>
                  <div className="invites-list">
                    {pendingInvites.map(invite => (
                      <div key={invite.id} className="invite-item">
                        <div>
                          <div className="invite-email">{invite.email}</div>
                          <div className="invite-meta">
                            Sent by {invite.inviterName} • Expires {new Date(invite.expiresAt).toLocaleDateString()}
                          </div>
                        </div>
                        {isOwner && (
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              className="btn btn-sm btn-outline"
                              onClick={() => handleResendInvite(invite.email, invite.id)}
                              title="Revoke and send a new invitation"
                            >
                              Re-send
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleRevokeInvite(invite.id)}
                            >
                              Revoke
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Current Members */}
              <div className="members-section">
                <h3>Members ({members.length})</h3>

                {/* Owner */}
                {owner && (
                  <div className="member-item owner-item">
                    <div className="member-info">
                      {owner.avatar && (
                        <img src={owner.avatar} alt={owner.name} className="member-avatar" />
                      )}
                      <div>
                        <div className="member-name">
                          {owner.name}
                          {owner.userId === currentUser?._id && ' (You)'}
                        </div>
                        <div className="member-email">{owner.email}</div>
                      </div>
                    </div>
                    <span className="role-badge owner-badge">Owner</span>
                  </div>
                )}

                {/* Collaborators */}
                {collaborators.map(member => (
                  <div key={member.id} className="member-item">
                    <div className="member-info">
                      {member.avatar && (
                        <img src={member.avatar} alt={member.name} className="member-avatar" />
                      )}
                      <div>
                        <div className="member-name">
                          {member.name}
                          {member.userId === currentUser?._id && ' (You)'}
                        </div>
                        <div className="member-email">{member.email}</div>
                      </div>
                    </div>
                    <div className="member-actions">
                      <span className="role-badge">Collaborator</span>
                      {isOwner && member.userId !== currentUser?._id && (
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleRemoveMember(member.userId, member.name)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="collaborator-actions">
                {isOwner && collaborators.length > 0 && (
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowTransferModal(true)}
                  >
                    Transfer Ownership
                  </button>
                )}
                {!isOwner && (
                  <button
                    className="btn btn-outline-danger"
                    onClick={handleLeaveProject}
                  >
                    Leave Project
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Close</button>
        </div>
      </div>

      {/* Transfer Ownership Modal */}
      {showTransferModal && (
        <div className="modal-overlay" onClick={() => setShowTransferModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Transfer Ownership</h2>
              <button className="modal-close" onClick={() => setShowTransferModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p>Select a collaborator to transfer ownership to. You will become a collaborator.</p>
              <div className="form-group">
                <label className="form-label">New Owner</label>
                <select
                  className="form-control"
                  value={selectedNewOwner}
                  onChange={(e) => setSelectedNewOwner(e.target.value)}
                >
                  <option value="">Select a collaborator...</option>
                  {collaborators.map(member => (
                    <option key={member.userId} value={member.userId}>
                      {member.name} ({member.email})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowTransferModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleTransferOwnership}>
                Transfer Ownership
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageCollaborators;
